import styles from "./lesson-title.module.css"

import IconAdd from "@shared/components/icons/Add"
import { cx } from "@shared/components/untitledui/util/cx"
import { PLACEHOLDERS, placeholderStyle } from "@shared/unified-tiptap/placeholders"
import { tracked } from "@supergrain/kernel/react"
import {
  NodeViewContent,
  NodeViewWrapper,
  useEditorState,
  type ReactNodeViewProps,
} from "@tiptap/react"
import { useState } from "react"
import { AddButton } from "../../editor-ui/add-button/AddButton"
import { CardPillButton } from "../../editor-ui/card-buttons/CardPillButton"
import { useLessonTitleChrome } from "../../lesson-title-chrome-context"
import { useUiContext } from "../../unified-tiptap-ui-context"

/**
 * The lesson title (h1), with an Add button pinned to its top right — the same pill design
 * as the fixed card buttons.
 */
export const LessonTitleComponent = tracked((props: ReactNodeViewProps) => {
  const { editor, getPos } = props
  const { setCardOverlayOpen, hideButtons } = useUiContext()
  // Host chrome, from its own context: absent outside a provider (template and unit editors,
  // read-only lessons), which renders the title with no gutter or trailing content.
  const { grip, trailing } = useLessonTitleChrome()
  const hasCards = useEditorState({
    editor,
    selector: ({ editor }) => editor.state.doc.childCount > 1,
  })
  const showAddButton = editor.isEditable && !hasCards && !hideButtons

  const [isAddOpen, setIsAddOpen] = useState(false)

  const setAddOpen = (isOpen: boolean) => {
    setIsAddOpen(isOpen)
    setCardOverlayOpen(isOpen)
  }

  // The Add menu inserts a card right after the lessonTitle — AddButton's addCard treats a
  // target pointing AT the lessonTitle as "insert after it". getPos() is the lessonTitle's
  // own position (0 = the doc's first child).
  const getAddTargetPos = (): number | null => getPos() ?? null

  return (
    <NodeViewWrapper className={showAddButton || grip || trailing ? "relative" : undefined}>
      {grip && (
        <div className={styles.lessonTitleGrip} contentEditable={false}>
          {grip}
        </div>
      )}
      {showAddButton && (
        <div className={cx("card-pill-shadow", styles.lessonTitleButtons)} contentEditable={false}>
          <AddButton
            editor={editor}
            mode="lessonTitle"
            getAddTargetPos={getAddTargetPos}
            getCardId={() => null}
            onOpenChange={setAddOpen}
          >
            <CardPillButton
              ariaLabel="Add lesson part"
              label="Add"
              icon={<IconAdd />}
              isMenuOpen={isAddOpen}
            />
          </AddButton>
        </div>
      )}
      {trailing && (
        <div className={styles.lessonTitleTrailing} contentEditable={false}>
          {trailing}
        </div>
      )}
      {/* Renders a div (like the card title's NodeViewContent); the h1 tag is only for
          serialization via the extension's renderHTML. The .lessonTitle class carries the
          heading styling either way. data-content-type keeps the LIVE dom findable by the
          same identity the serialized h1 carries — the planner's post-drop scroll watcher
          anchors on it (use-lesson-drag.ts). */}
      <NodeViewContent
        className={cx(styles.lessonTitle, trailing && styles.lessonTitleWithTrailing)}
        data-content-type="lessonTitle"
        style={placeholderStyle(PLACEHOLDERS.lessonTitle)}
      />
    </NodeViewWrapper>
  )
})
